/**
 * Image optimization utilities for responsive images
 */

export interface ResponsiveImageSet {
  jpg: {
    src: string;
    srcSet: string;
  };
  webp: {
    src: string;
    srcSet: string;
  };
  avif: {
    src: string;
    srcSet: string;
  };
}

/**
 * Generate responsive image URLs for different formats and sizes
 */
export const generateResponsiveImageSet = (
  basePath: string,
  baseName: string,
  sizes: number[] = [640, 800, 1200, 1920]
): ResponsiveImageSet => {
  const extension = basePath.split('.').pop();
  const pathWithoutExt = basePath.replace(`.${extension}`, '');

  // Generate srcSet strings
  const jpgSrcSet = sizes
    .map((size) => `${pathWithoutExt}-${size}w.${extension} ${size}w`)
    .join(', ');

  const webpSrcSet = sizes
    .map((size) => `${pathWithoutExt}-${size}w.webp ${size}w`)
    .join(', ');

  const avifSrcSet = sizes
    .map((size) => `${pathWithoutExt}-${size}w.avif ${size}w`)
    .join(', ');

  return {
    jpg: {
      src: basePath,
      srcSet: jpgSrcSet,
    },
    webp: {
      src: `${pathWithoutExt}.webp`,
      srcSet: webpSrcSet,
    },
    avif: {
      src: `${pathWithoutExt}.avif`,
      srcSet: avifSrcSet,
    },
  };
};

/**
 * Calculate optimal sizes attribute based on breakpoints
 */
export const calculateSizes = (config: {
  mobile?: string;
  tablet?: string;
  desktop?: string;
  default: string;
}): string => {
  const parts: string[] = [];

  if (config.mobile) {
    parts.push(`(max-width: 640px) ${config.mobile}`);
  }
  if (config.tablet) {
    parts.push(`(max-width: 1024px) ${config.tablet}`);
  }
  if (config.desktop) {
    parts.push(`(max-width: 1536px) ${config.desktop}`);
  }
  parts.push(config.default);

  return parts.join(', ');
};

/**
 * Preload critical images for better LCP
 */
export const preloadImage = (src: string, as: 'image' = 'image') => {
  const link = document.createElement('link');
  link.rel = 'preload';
  link.as = as;
  link.href = src;
  document.head.appendChild(link);
};

/**
 * Generate blur placeholder data URL
 */
export const generateBlurPlaceholder = (width: number, height: number): string => {
  return `data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 ${width} ${height}'%3E%3Cfilter id='b' color-interpolation-filters='sRGB'%3E%3CfeGaussianBlur stdDeviation='20'/%3E%3CfeColorMatrix values='1 0 0 0 0 0 1 0 0 0 0 0 1 0 0 0 0 0 100 -1' result='s'/%3E%3CfeFlood x='0' y='0' width='100%25' height='100%25'/%3E%3CfeComposite operator='out' in='s'/%3E%3CfeComposite in2='SourceGraphic'/%3E%3CfeGaussianBlur stdDeviation='20'/%3E%3C/filter%3E%3Cimage width='100%25' height='100%25' x='0' y='0' preserveAspectRatio='none' style='filter: url(%23b);' href='data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mN8/5+hHgAHggJ/PchI7wAAAABJRU5ErkJggg=='/%3E%3C/svg%3E`;
};
